import React from 'react';
import ProductDetailModal from '../../../components/products/ProductDetailModal';
import { getProductPrice } from '../utils/formatting';
import { getImageSrc } from '../utils/imageHelpers';

const hasGiftLevel = (product) => {
  return product.giftLevel && Object.keys(product.giftLevel).some(key => 
    key !== 'currency' && product.giftLevel[key] > 0
  );
};

export const OrderProductsStep = ({
  filteredProducts,
  products,
  selectedCurrency,
  selectedCountry,
  addToCart,
  cart,
  getCartCount,
  setStep,
  brokenImages,
  setBrokenImages,
  expandedUSPProducts,
  toggleUSPExpansion,
  selectedProductForDetails,
  setSelectedProductForDetails,
  browsingGroups,
  selectedBrowsingGroup,
  setSelectedBrowsingGroup,
  searchTerm,
  setSearchTerm,
  PROJECT_ID,
  REGION,
  upgradeInfo,
  setUpgradeInfo,
  showUpgradeModal,
  setShowUpgradeModal,
  checkingUpgrade,
  handleCheckUpgrade
}) => {
  const FN_BASE = PROJECT_ID ? `https://${REGION}-${PROJECT_ID}.cloudfunctions.net` : '';

  const handleAddToCart = (product, isGift = false) => {
    if (isGift) {
      // addGift logic is usually separate but if needed:
      // addGift(product, 1);
    } else {
      addToCart(product, 1);
    }
  };

  const handleShowProductDetails = (product) => {
    setSelectedProductForDetails(product);
  };

  const handleCloseProductDetails = () => {
    setSelectedProductForDetails(null);
  };

  const handleAddToCartFromDetails = (product) => {
    handleAddToCart(product, false);
    handleCloseProductDetails();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 pt-4 pb-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-4 sm:space-y-0">
        <div className="flex flex-col space-y-2">
          <h2 className="text-2xl font-bold text-[#011841]">Select Products</h2>
          <span className="text-sm text-[#033EA5] bg-[#F7FBFC] px-3 py-1 rounded-lg border border-[#19BFB7]/20 inline-block">
            📍 {selectedCountry.name} ({selectedCurrency})
          </span>
        </div>
        <div className="flex flex-col sm:flex-row items-end space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
          <button
            onClick={() => setShowUpgradeModal(true)}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-all duration-300 shadow-md hover:shadow-lg w-full sm:w-auto flex items-center justify-center"
          >
            🚀 Check Upgrade
          </button>
          <button
            onClick={() => setStep('cart')}
            className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 shadow-md hover:shadow-lg w-full sm:w-auto ${
              getCartCount() > 0 
                ? 'bg-[#033EA5] hover:bg-[#022b73] text-white' 
                : 'bg-gray-300 text-gray-600 cursor-not-allowed'
            }`}
          >
            🛒 View Cart {getCartCount() > 0 ? `(${getCartCount()})` : '(0)'}
          </button>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="mb-6 space-y-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search products by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 pl-10 border-2 border-[#19BFB7]/30 rounded-lg focus:border-[#19BFB7] focus:outline-none transition-colors bg-[#F7FBFC] text-[#011841] placeholder-[#033EA5]/60"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <svg className="w-5 h-5 text-[#033EA5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
        
        <div className="flex space-x-2 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4">
          <button
            onClick={() => setSelectedBrowsingGroup('all')}
            className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium transition-all duration-300 flex-shrink-0 ${
              selectedBrowsingGroup === 'all' 
                ? 'bg-[#033EA5] text-white shadow-md' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            All
          </button>
          {browsingGroups.map(group => (
            <button
              key={group}
              onClick={() => setSelectedBrowsingGroup(group)}
              className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium transition-all duration-300 flex-shrink-0 ${
                selectedBrowsingGroup === group 
                  ? 'bg-[#033EA5] text-white shadow-md' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {group}
            </button>
          ))}
        </div>
      </div>

      {/* Upgrade Status Banner */}
      {upgradeInfo?.eligible && (
        <div className="mb-6 p-4 bg-purple-50 border-2 border-purple-200 rounded-xl flex items-center justify-between animate-fade-in">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">✨</span>
            <div>
              <p className="text-purple-800 font-bold text-sm">Set Upgrade Pricing Active</p>
              <p className="text-purple-600 text-xs">
                Upgrading from: {upgradeInfo.highestSet.name} 
                {upgradeInfo.highestSet.purchasedAt && ` (Purchased: ${new Date(upgradeInfo.highestSet.purchasedAt).toLocaleDateString()})`}
                {upgradeInfo.highestSet.agentName && ` • Agent: ${upgradeInfo.highestSet.agentName}`}
              </p>
              <p className="text-purple-500 text-[10px] mt-1 italic">Customer Match Confidence: {upgradeInfo.confidence}%</p>
            </div>
          </div>
          <button 
            onClick={() => setUpgradeInfo(null)}
            className="text-purple-400 hover:text-purple-600 text-xs font-medium underline"
          >
            Reset Upgrade
          </button>
        </div>
      )}

      {/* Products Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredProducts.map(product => {
          const price = getProductPrice(product, selectedCurrency);
          const inCart = cart.find(item => item.id === product.id && !item.isGift);
          const imageKey = `product:${product.id}`;
          const hasImage = typeof product.imageUrl === 'string' && product.imageUrl.trim() !== '' && !brokenImages[imageKey];
          
          const hasUSP = product.USP && product.USP.trim() !== '';
          const uspText = hasUSP ? product.USP : '';
          const uspSnippet = hasUSP ? uspText.substring(0, 100) + (uspText.length > 100 ? '...' : '') : '';
          const shouldShowMore = hasUSP && uspText.length > 100;
          const isExpanded = expandedUSPProducts.has(product.id);
          
          return (
            <div key={product.id} className="bg-[#F7FBFC] rounded-xl shadow-lg border border-[#19BFB7]/20 p-6 hover:shadow-xl transition-all duration-300 flex flex-col h-full">
              <div className="flex justify-center mb-4">
                {hasImage ? (
                  <img 
                    src={getImageSrc(product.imageUrl, FN_BASE)}
                    alt={product.name}
                    className="w-full h-auto max-h-64 object-contain rounded-lg flex-shrink-0 bg-white sm:max-w-none md:max-w-xs lg:max-w-sm"
                    onError={() => setBrokenImages(prev => ({ ...prev, [imageKey]: true }))}
                  />
                ) : (
                  <div className="w-full h-64 rounded-lg flex-shrink-0 bg-gray-100 border border-gray-200 flex items-center justify-center text-center px-4">
                    <span className="text-sm font-medium text-gray-500">Missing image</span>
                  </div>
                )}
              </div>
              
              <div className="flex-1 flex flex-col">
                <div className="text-center mb-4">
                  <h3 className="font-semibold text-[#011841] text-lg mb-2">{product.name}</h3>
                  <p className="text-[#033EA5] text-sm mb-3">{product.category}</p>
                  <span className="text-xl font-bold text-[#033EA5] block mb-4">
                    {price}
                  </span>
                  {hasGiftLevel(product) && (
                    <span className="inline-block mb-4 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                      🎁 Gift Eligible
                    </span>
                  )}
                </div>
                
                {hasUSP && (
                  <div className="mb-4 flex-1">
                    <div className="bg-white rounded-lg p-3 border border-[#19BFB7]/10">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {isExpanded ? uspText : uspSnippet}
                      </p>
                      {shouldShowMore && (
                        <button
                          onClick={() => toggleUSPExpansion(product.id)}
                          className="text-[#033EA5] hover:text-[#022b73] text-xs font-medium mt-2 transition-colors"
                        >
                          {isExpanded ? 'Show less' : 'Show more'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="mt-auto flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <button
                    onClick={() => handleAddToCart(product, false)}
                    className="flex-1 bg-[#033EA5] hover:bg-[#022b73] text-white px-3 py-2 sm:px-4 sm:py-3 rounded-lg font-medium transition-all duration-300 shadow-md hover:shadow-lg text-sm"
                  >
                    🛒 {inCart ? `Add More` : 'Add to Cart'}
                  </button>
                  <button
                    onClick={() => handleShowProductDetails(product)}
                    className="flex-1 bg-white hover:bg-[#E8F4F8] text-[#033EA5] border-2 border-[#19BFB7]/30 hover:border-[#19BFB7] px-3 py-2 sm:px-4 sm:py-3 rounded-lg font-medium transition-all duration-300 text-sm"
                  >
                    📋 Details
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-between mt-8">
        <button
          onClick={() => setStep('country')}
          className="btn btn-secondary"
        >
          Back to Country
        </button>
        {getCartCount() > 0 && (
          <button
            onClick={() => setStep('cart')}
            className="btn btn-success"
          >
            Review Cart ({getCartCount()} items)
          </button>
        )}
      </div>

      {selectedProductForDetails && (
        <ProductDetailModal
          product={selectedProductForDetails}
          selectedCurrency={selectedCurrency}
          price={getProductPrice(selectedProductForDetails, selectedCurrency)}
          onClose={handleCloseProductDetails}
          onAddToCart={() => handleAddToCartFromDetails(selectedProductForDetails)}
        />
      )}

      {/* Upgrade Search Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-[#011841]">Check Upgrade Eligibility</h3>
                <button onClick={() => setShowUpgradeModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Enter the customer's email or phone number to check if they have purchased a SET before and are eligible for an upgrade.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <input
                    type="email"
                    id="upgrade-email"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19BFB7] focus:border-transparent outline-none"
                    placeholder="customer@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number (with +code)</label>
                  <input
                    type="tel"
                    id="upgrade-phone"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19BFB7] focus:border-transparent outline-none"
                    placeholder="+97333123456"
                  />
                </div>
                <div className="pt-4">
                  <button
                    onClick={() => {
                      const email = document.getElementById('upgrade-email').value;
                      const phone = document.getElementById('upgrade-phone').value;
                      handleCheckUpgrade(email, phone);
                    }}
                    disabled={checkingUpgrade}
                    className="w-full bg-[#033EA5] hover:bg-[#022b73] text-white py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg flex items-center justify-center"
                  >
                    {checkingUpgrade ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Searching...
                      </>
                    ) : 'Search Customer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
